import { useMemo, useRef, useState } from "react"
import { describeError } from "../../api/http"
import { categoryWrites } from "../../api/mutations"
import { useGroupCategories, useWebCategories } from "../../api/queries"
import type { ProductGroup, WebCategory } from "../../api/types"
import type { GroupRights } from "../../auth/rights"
import { ActionNotes, Note } from "../../components/Note"
import { useEscape } from "../../hooks/useEscape"
import { indexCategories } from "../../lib/categories"
import { useWriter } from "./useWriter"

/** Категория, уже назначенная группе. `ids` — путь сверху вниз, он же
 *  заполняет каскад при замене. */
interface Row {
	id: number
	ids: number[]
	parts: string[]
	isMain: boolean
}

/**
 * Вкладка «Категории»: категории группы на сайте.
 *
 * Дерево общее с российской стороной — 589 узлов, все 409 используемых категорий
 * встречаются и у РФ, и у Риги. Отдельного экрана дерева в рижском интерфейсе нет
 * (решение №5 плана), здесь категории только назначаются группе.
 *
 * Категорию выбирают каскадом сверху вниз, как в ECM
 * (`AdCategorySelectRow.tsx`): список верхнего уровня, за ним список детей
 * выбранного узла и так до листа. Промежуточный уровень назначить нельзя — бэк
 * отвечает `500` «Category has children», поэтому кнопка добавления оживает
 * только на листе.
 *
 * Печатные каталоги не показываем: их ведёт российская сторона, латвийским
 * группам они в ECM и не показывались (`groupPageAuthSelectors.ts:234-240`),
 * а править их бэк позволяет только `ContentManagerRF`.
 */
export default function CategoriesTab({
	group,
	languageId,
	rights,
	isForeign,
	siteName,
}: {
	group: ProductGroup
	languageId: number
	rights: GroupRights
	isForeign: boolean
	/** Имя выбранного сайта — только для заголовка: дерево у сайтов Риги общее. */
	siteName?: string
}) {
	const tree = useWebCategories(languageId)
	const current = useGroupCategories(group.id, languageId)
	const writer = useWriter(group.id)

	/** Выбранный путь по дереву: id на каждом уровне, сверху вниз. */
	const [path, setPath] = useState<number[]>([])
	const [replacing, setReplacing] = useState<Row | null>(null)
	const firstLevel = useRef<HTMLSelectElement>(null)

	const roots = useMemo(() => tree.data ?? [], [tree.data])

	// названия нужны и листьям, и промежуточным уровням: категории группы
	// приходят одними id
	const { names, leaves } = useMemo(() => indexCategories(roots), [roots])

	const rows = useMemo<Row[]>(
		() =>
			(current.data ?? [])
				.map(item => {
					// путь приходит от листа к верхнему уровню — разворачиваем
					const ids = [...item.categoryPath].reverse()
					return {
						id: ids[ids.length - 1],
						ids,
						parts: ids.map(id => names.get(id) ?? `#${id}`),
						isMain: item.mainCategory === true,
					}
				})
				.sort((a, b) =>
					a.isMain === b.isMain
						? a.parts.join(" ").localeCompare(b.parts.join(" "))
						: a.isMain
							? -1
							: 1
				),
		[current.data, names]
	)

	/** Списки каскада: верхний уровень плюс по списку на каждый выбранный узел
	 *  с детьми. Последний список — тот, в котором ещё ничего не выбрано или
	 *  выбран лист. */
	const levels = useMemo(() => {
		const levels: WebCategory[][] = []
		let options = roots
		while (options.length > 0) {
			levels.push(options)
			const chosen = options.find(node => node.id === path[levels.length - 1])
			if (!chosen || chosen.children.length === 0) break
			options = chosen.children
		}
		return levels
	}, [roots, path])

	/** Узел на конце выбранного пути. */
	const picked = useMemo(() => {
		let options = roots
		let node: WebCategory | null = null
		for (const id of path) {
			const found = options.find(item => item.id === id)
			if (!found) return null
			node = found
			options = found.children
		}
		return node
	}, [roots, path])

	const leaf = picked && picked.children.length === 0 ? picked : null
	const duplicate = !!leaf && rows.some(row => row.id === leaf.id)

	const busy = writer.busy || current.isFetching
	const loading = tree.isLoading || current.isLoading

	// Убрать последнюю категорию ERP не умеет: она же главная, а замену главной
	// он ищет среди оставшихся и падает на пустом списке (500,
	// `GroupCategoryManager.cs:211-213`, проверено на стенде — правки при этом
	// откатываются). Единственный способ перенести группу — замена.
	const last = rows.length === 1

	const reset = () => {
		setPath([])
		setReplacing(null)
	}

	// Esc делает то же, что кнопка «Отмена»: сбрасывает каскад и выходит из
	// режима замены. Списки уровней — обычные select, их выпадашку Esc закрывает
	// сам браузер, до окна событие в этот момент не доходит
	useEscape(!!replacing || path.length > 0, reset)

	const submit = () => {
		if (!leaf) return
		const target = replacing
		const categoryId = leaf.id
		reset()
		void writer.run(() =>
			target
				? categoryWrites.change(group.id, target.id, categoryId)
				: categoryWrites.add(group.id, categoryId)
		)
	}

	const startReplace = (row: Row) => {
		setReplacing(row)
		// каскад заполняем текущим путём строки: чаще всего меняют последний
		// уровень, а не всю ветку
		setPath(row.ids)
		firstLevel.current?.focus()
	}

	return (
		<div className="panel">
			<div className="panel__head">
				<span>
					Категории на {siteName ?? "сайте"}
					{rows.length > 0 && ` · ${rows.length}`}
				</span>
				<span className="panel__spacer" />
				<span className="muted cat-count">
					{tree.isLoading
						? "дерево загружается…"
						: `категорий последнего уровня: ${leaves}`}
				</span>
			</div>

			{(writer.error || tree.isError) && (
				<div className="cat-alerts">
					<ActionNotes action={writer} />
					{tree.isError && (
						<Note tone="error">
							Не удалось загрузить дерево категорий: {describeError(tree.error)}
						</Note>
					)}
				</div>
			)}

			{rights.webCategories && (
				<div className={"cat-bar" + (replacing ? " cat-bar--replace" : "")}>
					<span className="cat-bar__label">
						{replacing
							? `Заменить «${replacing.parts[replacing.parts.length - 1]}» на`
							: "Добавить"}
					</span>

					{levels.map((options, level) => (
						<select
							key={level}
							ref={level === 0 ? firstLevel : undefined}
							className="select cat-level"
							disabled={busy}
							value={path[level] ?? ""}
							onChange={event => {
								const value = event.target.value
								// сбрасываем всё, что было выбрано ниже: ветка сменилась
								setPath(
									value
										? [...path.slice(0, level), Number(value)]
										: path.slice(0, level)
								)
							}}
						>
							<option value="">
								{level === 0 ? "— раздел —" : "— уровень " + (level + 1) + " —"}
							</option>
							{options.map(node => (
								<option key={node.id} value={node.id}>
									{node.name}
								</option>
							))}
						</select>
					))}

					<button
						className="btn btn--primary"
						disabled={busy || !leaf || duplicate}
						title={
							duplicate
								? "Категория уже добавлена"
								: leaf
									? undefined
									: "Выберите категорию последнего уровня"
						}
						onClick={submit}
					>
						{replacing ? "Заменить" : "Добавить"}
					</button>

					{(replacing || path.length > 0) && (
						<button
							className="btn"
							disabled={busy}
							title="Сбросить выбор (Esc)"
							onClick={reset}
						>
							Отмена
						</button>
					)}

					<span className="muted cat-bar__hint">
						{duplicate
							? "уже добавлена"
							: leaf
								? `ID ${leaf.id}`
								: "выберите категорию последнего уровня"}
					</span>
				</div>
			)}

			{loading && (
				<div className="cat-state">
					<span className="spinner" />
				</div>
			)}

			{!loading && rows.length === 0 && (
				<div className="cat-state muted">
					Группа не привязана ни к одной категории — на сайте её не будет.
				</div>
			)}

			{rows.length > 0 && (
				<ul className="site-cats">
					{rows.map(row => (
						<li key={row.id} className="site-cats__row">
							<button
								className={"cat-star" + (row.isMain ? " cat-star--on" : "")}
								disabled={!rights.webCategories || row.isMain || busy}
								title={
									row.isMain
										? "Главная категория: в ней товары группы живут на сайте"
										: "Сделать главной категорией"
								}
								onClick={() =>
									void writer.run(() => categoryWrites.setMain(group.id, row.id))
								}
							>
								{row.isMain ? "★" : "☆"}
							</button>

							<span className="site-cats__path">
								{row.parts.map((part, at) => (
									<span key={at}>
										{at > 0 && <span className="site-cats__sep">→</span>}
										{at === row.parts.length - 1 ? <b>{part}</b> : part}
									</span>
								))}
							</span>

							{row.isMain && <span className="tag tag--own">главная</span>}
							<span className="mono muted site-cats__id">{row.id}</span>

							{rights.webCategories && (
								<span className="site-cats__act">
									<button
										className="cat-mini"
										title="Заменить эту категорию другой"
										disabled={busy}
										onClick={() => startReplace(row)}
									>
										⇄
									</button>
									<button
										className="cat-mini cat-mini--danger"
										title={
											last
												? "Единственную категорию убрать нельзя — ERP отвечает ошибкой. Чтобы перенести группу, замените категорию кнопкой ⇄"
												: "Убрать группу из этой категории"
										}
										disabled={busy || last}
										onClick={() =>
											void writer.run(() =>
												categoryWrites.remove(group.id, row.id)
											)
										}
									>
										✕
									</button>
								</span>
							)}
						</li>
					))}
				</ul>
			)}

			<details className="cat-help">
				<summary>Как это работает</summary>
				<p>
					<b>Главная категория</b> — та, в которой товары группы лежат на сайте:
					по ней строится путь в каталоге и адрес страницы. Остальные показывают
					ту же группу дополнительно. Главной может быть только одна; первая
					добавленная становится ею сама, а если убрать главную, ERP назначит
					следующую.
				</p>
				<p>
					Категорию выбирают сверху вниз: раздел → подраздел → категория.
					Назначить можно только последний уровень — промежуточные ERP не
					принимает. Последнюю категорию нельзя убрать, но её можно заменить
					другой — кнопка <b>⇄</b> в строке.
				</p>
				{isForeign && (
					<p>
						Категории на сайте — единственное, что Рига ведёт и в РФ-группах
						наравне со своими. Печатные каталоги здесь не показываются: их ведёт
						российская сторона.
					</p>
				)}
			</details>
		</div>
	)
}
